import React from "react";

import { Check, Eye, Upload } from "lucide-react";

import { FileIcon } from "@/components/ui/file-icon";
import { stripBundlePrefix } from "@/utils/bundle-name";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import { ReactComponent as SortIcon } from "@assets/svg/sort.svg";

import {
  DeleteFileConfirmation,
  RegenerateFileConfirmation,
} from "@components/FilesListDataTable";
import { Button } from "@components/ui/button";
import { Checkbox } from "@components/ui/checkbox";
import { ScrollArea } from "@components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { formatNullableDateToLocal } from "@utils/dateUtils";

import { formatDocumentCategory } from "../utils/classificationMapper";

type DocumentChronologicalViewProps = {
  files: DocSorterFileSchema[];
  onViewFile: (file: DocSorterFileSchema | null) => void;
  onViewSummary: (file: DocSorterFileSchema) => void;
  isLoading: boolean;
  selectedDocumentId?: string;
  showSummaryPanel?: boolean;
  isMergeMode?: boolean;
  mergeSelectedFiles?: string[];
  onSelectFileForMerge?: (fileId: string, selected: boolean) => void;
  onSelectAllForMerge?: (selected: boolean) => void;
};

type DocumentChronologicalViewColumn = {
  id: string;
  header: string;
  accessor: (file: DocSorterFileSchema) => React.ReactNode;
  sortable?: boolean;
  width: string;
};

export const DocumentChronologicalView = ({
  files,
  onViewFile,
  onViewSummary,
  isLoading,
  selectedDocumentId,
  showSummaryPanel = false,
  isMergeMode = false,
  mergeSelectedFiles = [],
  onSelectFileForMerge,
  onSelectAllForMerge,
}: DocumentChronologicalViewProps) => {
  const columns: DocumentChronologicalViewColumn[] = [
    {
      id: "date",
      header: "Document Date",
      accessor: (file) => formatNullableDateToLocal(file.date),
      width: showSummaryPanel ? "w-2/5" : "w-1/5",
    },
    {
      id: "description",
      header: "AI Description",
      accessor: (file) => (
        <div className="line-clamp-2">
          {file.short_summary || "No description available"}
        </div>
      ),
      width: "w-1/4",
    },
    {
      id: "category",
      header: "Category",
      accessor: (file) => {
        const displayText =
          formatDocumentCategory(file.classification) || "Uncategorized";
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="truncate">{displayText}</div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{displayText}</p>
            </TooltipContent>
          </Tooltip>
        );
      },
      width: "w-1/5",
    },
    {
      id: "name",
      header: "File Name",
      accessor: (file) => (
        <div className="flex items-center gap-2">
          <FileIcon
            fileName={stripBundlePrefix(file.name)}
            className="h-5 w-5 flex-shrink-0"
          />
          <span className="truncate font-medium text-foreground">
            {stripBundlePrefix(file.name)}
          </span>

          {/* Status icons */}
          {file.status === "EXPORTED" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <figure className="rounded-full bg-primary-50 p-1">
                  <Check className="size-3 text-primary" />
                </figure>
              </TooltipTrigger>
              <TooltipContent>
                <p>This file has been exported to Report Builder.</p>
              </TooltipContent>
            </Tooltip>
          )}

          {file.status === "EXPORTING" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <figure className="rounded-full bg-warning-50 p-1">
                  <Upload className="size-3 animate-pulse text-warning" />
                </figure>
              </TooltipTrigger>
              <TooltipContent>
                <p>This file is currently being exported to Report Builder.</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      ),
      width: showSummaryPanel ? "w-3/5" : "w-1/5",
    },
    ...(showSummaryPanel
      ? []
      : ([
          {
            id: "actions",
            header: "Actions",
            accessor: (file) => (
              <div
                className="flex items-center justify-start gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewSummary(file);
                  }}
                  className="h-8 w-8 p-[4px] text-primary"
                  title="View Full Summary"
                >
                  <Eye className="size-5 h-4 w-4" />
                </Button>

                {/* Regenerate */}
                <div onClick={(e) => e.stopPropagation()}>
                  <RegenerateFileConfirmation
                    file={{
                      id: file.id,
                      name: file.name,
                      classification: file.classification || "",
                      date: file.date || undefined,
                      case: file.case,
                    }}
                  />
                </div>

                <div onClick={(e) => e.stopPropagation()}>
                  <DeleteFileConfirmation
                    file={{
                      id: file.id,
                      name: file.name,
                      classification: file.classification || "",
                      date: file.date || undefined,
                      case: file.case,
                    }}
                  />
                </div>
              </div>
            ),
            width: "w-[15%]",
          },
        ] as DocumentChronologicalViewColumn[])),
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-sm text-foreground-muted">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-foreground-muted">No documents found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full space-y-4">
      <ScrollArea className="h-full">
        <div className="sticky top-0 z-10 flex items-center gap-4 border-b bg-background p-5">
          {isMergeMode ? (
            <div className="w-8">
              <Checkbox
                checked={
                  mergeSelectedFiles.length === files.length && files.length > 0
                }
                onCheckedChange={(checked) => {
                  onSelectAllForMerge?.(checked === true);
                }}
              />
            </div>
          ) : (
            <div className="w-6"></div>
          )}

          {columns.map((column) => (
            <div
              key={column.id}
              className={cn(
                "flex text-sm font-medium text-foreground-muted",
                column.width
              )}
            >
              <div className="flex items-center">
                {column.header}
                {column.sortable && (
                  <span className="ml-2 inline-block">
                    <SortIcon className="h-4 w-4 text-foreground-muted" />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3 p-2">
          {files.map((file) => (
            <div
              key={file.id}
              className={cn(
                "mt-[15px] flex cursor-pointer items-center rounded-xl bg-background p-2",
                {
                  "bg-accent-50": selectedDocumentId === file.id,
                  "hover:bg-primary/10": selectedDocumentId !== file.id,
                }
              )}
              onClick={() => {
                if (!isMergeMode) {
                  if (selectedDocumentId === file.id) {
                    onViewFile(null);
                  } else {
                    onViewFile(file);
                  }
                }
              }}
            >
              {isMergeMode ? (
                <div className="w-8 px-2">
                  <Checkbox
                    checked={mergeSelectedFiles.includes(file.id)}
                    onCheckedChange={(checked) => {
                      onSelectFileForMerge?.(file.id, checked === true);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ) : (
                <div className="w-6"></div>
              )}

              {columns.map((column) => (
                <div
                  key={column.id}
                  className={cn(
                    "overflow-hidden p-3 text-sm text-foreground",
                    column.width
                  )}
                >
                  {column.accessor(file)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
